import React, { useState, useEffect, useRef } from 'react';
import '../../App.css'; // Update path for styles
import { drawLines } from './drawLines'; // Update path for drawLines
import { isPointNearLine } from './utils'; // Update path for utilities
import ColorBar from './ColorBar'; // Update path for ColorBar component
import Slider from './Slider'; // Update path for Slider component

function Site2() {
  const [jsonData, setJsonData] = useState(null);
  const [error, setError] = useState(null);
  const [sliderValue, setSliderValue] = useState(0);
  const [timeStepCount, setTimeStepCount] = useState(0);
  const [worstColor, setWorstColor] = useState('green');
  const [sliderBackground, setSliderBackground] = useState('');
  const [selectedLineInfo, setSelectedLineInfo] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [timeUnit, setTimeUnit] = useState(null);
  const [infoPanelVisible, setInfoPanelVisible] = useState(false);
  const [buttons, setButtons] = useState([]);
  const canvasRef = useRef(null);
  const imageRef = useRef(new Image());
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imagePath, setImagePath] = useState(null);

  useEffect(() => {
    console.log("[DEBUG] Fetching stored image...");
    fetch("https://recogtoolbox.se/api/get-stored-image")
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        const imageURL = URL.createObjectURL(blob);
        setImagePath(imageURL);
        console.log("[DEBUG] Image loaded successfully:", imageURL);
      })
      .catch(error => {
        console.error("[ERROR] Fetching stored image failed:", error);
      });
  }, []);

  // ✅ Fetch stored JSON from backend when site2 loads
  useEffect(() => {
    console.log("[DEBUG] Fetching stored JSON...");

    fetch("https://recogtoolbox.se/api/get-stored-json")
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch JSON: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log("[DEBUG] JSON data received:", data);
        setJsonData(data);
        setTimeStepCount(Object.keys(data).length);

        // ✅ Extract and store the time unit
        if (data.time_unit) {
          setTimeUnit(data.time_unit);
        }
      })

      .catch(error => {
        console.error("[ERROR] Fetching JSON failed:", error);
        setError(error.message);
      });
  }, []);

  const formatTime = (timeValue) => {
    let totalSeconds = timeValue; // Assume timeValue is in the given timeUnit

    // Convert everything to seconds
    const conversionFactor = {
      "s": 1,
      "m": 60,
      "h": 3600,
      "d": 86400
    };

    if (timeUnit in conversionFactor) {
      totalSeconds *= conversionFactor[timeUnit];
    }

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return `${days} [days] ${hours} [hours] ${minutes} [minutes] ${seconds} [seconds]`;
  };

  useEffect(() => {
    if (!imagePath || !jsonData) return; // Ensure both the image and JSON data are available before proceeding
    console.log("[DEBUG] Setting image source to:", imagePath);
    const img = imageRef.current;
    img.src = imagePath;
    img.onload = () => {
      if (!canvasRef.current) {
        console.error("[ERROR] Canvas is null, cannot draw image.");
        return;
      }
      setImageSize({ width: img.width, height: img.height });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      console.log("[DEBUG] Image loaded and drawn successfully.");
      generateSliderBackground(); // ✅ Ensure the color bar updates
    };
    img.onerror = (err) => {
      console.error("[ERROR] Failed to load image:", err);
    };
  }, [imagePath, jsonData]); // ✅ Now runs when imagePath or jsonData changes
  
  

  useEffect(() => {
    if (!jsonData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, img.width, img.height);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (jsonData[sliderValue]) {
      const currentData = jsonData[sliderValue];
      setCurrentTime(formatTime(currentData.time));
      const scaleX = canvas.width / imageSize.width;
      const scaleY = canvas.height / imageSize.height;
      const linesToDraw = Object.keys(currentData)
        .filter(key => key !== 'time')
        .map(key => ({
          from: [currentData[key].coord_from[0] * scaleX, currentData[key].coord_from[1] * scaleY],
          to: [currentData[key].coord_to[0] * scaleX, currentData[key].coord_to[1] * scaleY],
          color: getColorForLine(currentData[key].average_stds_of),
          extraInfo: { key, extra_dict: currentData[key].extra_dict },
        }));

      drawLines(ctx, linesToDraw);
      setWorstColor(determineWorstColor(linesToDraw.map(line => line.color)));

      const generatedButtons = linesToDraw.map((line, index) => {
        const canvasRect = canvas.getBoundingClientRect();
        const midpoint = {
          x: (line.from[0] + line.to[0]) / 2,
          y: (line.from[1] + line.to[1]) / 2,
        };
        return (
          <button
            key={index}
            style={{
              position: 'absolute',
              left: `${canvasRect.left + midpoint.x}px`,
              top: `${canvasRect.top + midpoint.y}px`,
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              cursor: 'pointer',
            }}
            onClick={() => handleLineClick(line.extraInfo)}
          >
            i
          </button>
        );
      });

      setButtons(generatedButtons);
    }
  }, [sliderValue, imageSize, jsonData]);

  const handleLineClick = (extraInfo) => {
    const { extra_dict } = extraInfo;
    if (extra_dict && extra_dict.pallet_nr && extra_dict.travel_time) {
      const lineInfo = extra_dict.pallet_nr.map((pallet_nr, index) => ({
        pallet_nr,
        travel_time: extra_dict.travel_time[index],
      }));
      setSelectedLineInfo(lineInfo);
      setInfoPanelVisible(true);
    }
  };

  const handleSliderChange = event => {
    setSliderValue(parseInt(event.target.value));
  };

  const handleStepChange = direction => {
    setSliderValue(prevValue => {
      if (direction === 'left' && prevValue > 0) return prevValue - 1;
      if (direction === 'right' && prevValue < timeStepCount - 1) return prevValue + 1;
      return prevValue;
    });
  };

  const getColorForLine = average_stds_of => {
    if (average_stds_of < 0) return 'green';
    if (average_stds_of < 0.5) return 'yellow';
    if (average_stds_of < 1) return 'orange';
    return 'red';
  };

  const determineWorstColor = colors => {
    if (colors.includes('red')) return 'red';
    if (colors.includes('orange')) return 'orange';
    if (colors.includes('yellow')) return 'yellow';
    return 'green';
  };

  const generateSliderBackground = () => {
    if (!jsonData) return;
    const colors = Object.keys(jsonData).map(timeStep => {
      const currentData = jsonData[timeStep];
      const lineColors = Object.keys(currentData)
        .filter(key => key !== 'time')
        .map(key => getColorForLine(currentData[key].average_stds_of));
      return determineWorstColor(lineColors);
    });

    const gradientStops = colors.map((color, index) => {
      const percentage = (index / (colors.length - 1)) * 100;
      return `${color} ${percentage}%`;
    });

    setSliderBackground(`linear-gradient(to right, ${gradientStops.join(', ')})`);
  };

  return (
    <div style={styles.container}>
      <canvas ref={canvasRef} width={imageSize.width} height={imageSize.height} style={styles.canvas} />
      <div style={styles.timeDisplay}>
        Time: {currentTime}
      </div>
      <ColorBar sliderBackground={sliderBackground} />
      <Slider timeStepCount={timeStepCount} sliderValue={sliderValue} handleSliderChange={handleSliderChange} handleStepChange={handleStepChange} worstColor={worstColor} />
      {buttons}
      {infoPanelVisible && (
        <div style={styles.infoPanel}>
          <button style={styles.closeButton} onClick={() => setInfoPanelVisible(false)}>Close</button>
          <h3>Line information</h3>
          <pre>{JSON.stringify(selectedLineInfo, null, 2)}</pre>
        </div>
      )}
      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', margin: 0, overflow: 'hidden', backgroundColor: 'rgba(0, 0, 0, 0.5)' }, // Make the background dimmed too
  canvas: { border: '1px solid black', pointerEvents: 'none', position: 'relative' },
  timeDisplay: { fontSize: '18px', marginBottom: '5px', fontWeight: 'bold' },
  infoPanel: {
    position: 'fixed',
    right: 0,
    top: 0,
    width: '300px',
    height: '100%',
    backgroundColor: '#fff',
    borderLeft: '1px solid black',
    padding: '20px',
    boxShadow: '0px 0px 10px rgba(0,0,0,0.3)',
  },
  closeButton: {
    background: 'red',
    color: '#fff',
    border: 'none',
    padding: '5px 10px',
    cursor: 'pointer',
  },
  infoTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  infoTableHeader: {
    fontWeight: 'bold',
  },
  infoTableCell: {
    border: '1px solid black',
    padding: '8px',
  },
};

export default Site2;